///
/// EarthBrowser
///
/// Created by Matt Giger on 10/26/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Environment facts for the storm column from the sea surface temperature
/// field: what the storm is sitting on, how long the forecast track stays
/// over water warm enough to sustain it, and the cold wake it has left
/// behind. Pure functions over a sampler so they stay testable.
///

import { type StormTrackPoint } from "../HurricanesTable"

/** The classical genesis / maintenance threshold */
export const cWarmWaterThresholdC = 26.5
/** Sampling step along the tracks */
const cTrackStepMs = 60 * 60_000

export interface SstSampler {
	sampleNearWater(lat: number, lon: number): number
}

export interface EnvironmentFacts {
	/** SST under the storm center, °C; undefined when the center has no water nearby */
	sstUnderCenterC?: number
	/**
	 * First forecast time the center is over water cooler than 26.5 °C (or
	 * over land, which ends the warm-water run); undefined when the whole
	 * forecast stays over warm water
	 */
	warmWaterEndsT?: number
	/** True when the track is already over cool water or land now */
	alreadyCool: boolean
	/** Mean SST change along the last day of the observed track between two frames, °C (negative = cooling) */
	coldWakeC?: number
}

/** Position on a track at a time, linear between points (fine at hourly steps) */
function positionAt(track: StormTrackPoint[], tMs: number): { lat: number; lon: number } | undefined {
	if (track.length === 0) return undefined
	if (tMs <= track[0].t) return track[0]
	for (let i = 1; i < track.length; i++) {
		const a = track[i - 1]
		const b = track[i]
		if (tMs <= b.t) {
			const f = b.t > a.t ? (tMs - a.t) / (b.t - a.t) : 0
			let dLon = b.lon - a.lon
			if (dLon > 180) dLon -= 360
			if (dLon < -180) dLon += 360
			return { lat: a.lat + (b.lat - a.lat) * f, lon: a.lon + dLon * f }
		}
	}
	return track[track.length - 1]
}

/**
 * Facts from the newest frame (`now`) and, for the wake, an older frame
 * (`before`, ideally a day earlier). The storm's present position comes
 * from the advisory, the future from the forecast, the past from the
 * observed track
 */
export function environmentFacts(now: SstSampler, before: SstSampler | undefined, center: { lat: number; lon: number }, observed: StormTrackPoint[], forecast: StormTrackPoint[]): EnvironmentFacts {
	const facts: EnvironmentFacts = { alreadyCool: false }
	const here = now.sampleNearWater(center.lat, center.lon)
	if (Number.isFinite(here)) facts.sstUnderCenterC = Math.round(here * 10) / 10
	facts.alreadyCool = !Number.isFinite(here) || here < cWarmWaterThresholdC

	// Walk the forecast hourly until the water cools or the center is inland
	if (!facts.alreadyCool && forecast.length >= 1) {
		const start = forecast[0].t
		const end = forecast[forecast.length - 1].t
		for (let t = start; t <= end; t += cTrackStepMs) {
			const p = positionAt(forecast, t)
			if (!p) break
			const sst = now.sampleNearWater(p.lat, p.lon)
			if (!Number.isFinite(sst) || sst < cWarmWaterThresholdC) {
				facts.warmWaterEndsT = t
				break
			}
		}
	}

	// Cold wake: the same points on the last day of the observed track in both frames
	if (before && observed.length >= 2) {
		const last = observed[observed.length - 1].t
		let sum = 0
		let n = 0
		for (let t = last - 24 * 3600_000; t <= last; t += cTrackStepMs) {
			const p = positionAt(observed, t)
			if (!p) continue
			const a = before.sampleNearWater(p.lat, p.lon)
			const b = now.sampleNearWater(p.lat, p.lon)
			if (Number.isFinite(a) && Number.isFinite(b)) {
				sum += b - a
				n++
			}
		}
		if (n >= 3) facts.coldWakeC = Math.round((sum / n) * 10) / 10
	}
	return facts
}
